/* ─────────────────────────────────────────────────────────────────────────
   jiraApi.js — THE FROZEN INTERFACE (the only seam between Eric's UI and Jira)

   Eric's dashboard talks to Jira through exactly these functions. In the Claude
   artifact they were model-mediated calls to api.anthropic.com with the Atlassian
   MCP. Here they are thin `fetch` calls to the BFF (`/api/*`), which proxies the
   Jira Cloud REST API and returns the SAME JSON shapes the components consume.

   CONTRACT: a regenerated JSX must keep calling these signatures. The BFF must
   keep returning the canonical issue object (see developer-handoff-checklist §2).
   Change neither side without changing the other + the contract tests.

   Read path returns the PARSED canonical issue object (the server does the ADF
   flattening, attachment-URL rebuild, and remote-link classification that the
   model used to do). Write path returns { success, message }.
   ───────────────────────────────────────────────────────────────────────── */

// Same-origin by default: CloudFront serves the SPA at exec.costarpartners.net and
// routes /api/* to API Gateway, so the browser sees one origin and the __Host-
// session cookie flows automatically. Override with VITE_API_BASE for local dev
// (e.g. "http://localhost:3001") where the BFF runs on a different port.
const API_BASE = (import.meta.env?.VITE_API_BASE ?? "").replace(/\/$/, "");

// Same 20s budget the artifact used: past this, surface "Connection issue · Retry"
// instead of a silent spin.
const JIRA_TIMEOUT_MS = 20000;

/* When the session is gone/expired the BFF answers 401. The only correct response
   is to send the user (back) through the Entra→3LO login. Full-page nav so the
   OAuth redirect dance can run. Guarded so we only bounce once. */
let redirectingToLogin = false;
function goToLogin() {
  if (redirectingToLogin) return;
  redirectingToLogin = true;
  const here = window.location.pathname + window.location.search;
  window.location.assign(`${API_BASE}/api/auth/login?returnTo=${encodeURIComponent(here)}`);
}

/* Core fetch helper: same-origin cookie auth, JSON in/out, abortable timeout.
   Throws on timeout (so callers can show Retry); throws AuthError on 401 after
   kicking off the login redirect; returns parsed JSON otherwise. */
class AuthError extends Error {}

async function apiFetch(path, { method = "GET", body, timeoutMs = JIRA_TIMEOUT_MS } = {}) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const resp = await fetch(`${API_BASE}/api${path}`, {
      method,
      headers: body ? { "Content-Type": "application/json" } : undefined,
      body: body ? JSON.stringify(body) : undefined,
      credentials: "include",
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (resp.status === 401) {
      goToLogin();
      throw new AuthError("Not authenticated");
    }
    if (!resp.ok) {
      const text = await resp.text().catch(() => "");
      throw new Error(`Jira proxy ${resp.status}: ${text.slice(0, 200)}`);
    }
    // 204 no-content (rare) → null
    if (resp.status === 204) return null;
    return await resp.json();
  } catch (err) {
    clearTimeout(timeoutId);
    if (err.name === "AbortError") {
      throw new Error(`Jira didn't respond within ${JIRA_TIMEOUT_MS / 1000}s. The connection may be slow or unavailable — try again.`);
    }
    throw err;
  }
}

/* ─── READ PATH ─────────────────────────────────────────────────────────── */

/* Bulk search. The BFF runs /rest/api/3/search/jql with bounded JQL + nextPageToken
   pagination and returns the parsed canonical issue object array. On a clean failure
   it returns [] (the UI treats a non-array as a soft failure and retries). On a hard
   timeout it throws (so fetchData stops spinning and shows Retry). */
export async function jiraSearch(jql, maxResults = 100) {
  try {
    const data = await apiFetch(`/jira/search`, {
      method: "POST",
      body: { jql, maxResults },
    });
    return Array.isArray(data?.issues) ? data.issues : (Array.isArray(data) ? data : []);
  } catch (err) {
    if (err instanceof AuthError) return []; // login redirect already in flight
    if (String(err.message || "").includes("didn't respond within")) throw err; // let timeout bubble to Retry UI
    console.error("Jira search failed:", err);
    return [];
  }
}

/* Retry wrapper — preserved from the artifact. The cold-start "first load hangs,
   Refresh fixes it" behavior was an MCP warm-up artifact and shouldn't recur on
   direct REST, but the wrapper is cheap insurance against a transient 5xx that
   surfaces as a non-array. Same signature so callers are unchanged. */
export async function jiraSearchReliable(jql, maxResults = 100, retries = 2) {
  let res = await jiraSearch(jql, maxResults);
  let attempt = 0;
  while (!Array.isArray(res) && attempt < retries) {
    attempt++;
    await new Promise((r) => setTimeout(r, 600 + attempt * 700));
    res = await jiraSearch(jql, maxResults);
  }
  return res;
}

/* Remote links for ONE issue. Jira's bulk search does not return remote/web links,
   so these are fetched per issue (gotcha #1). Called lazily when a panel opens.
   Returns { confluenceLinks:[{title,url}], webLinks:[{title,url}] }. */
export async function jiraGetRemoteLinks(issueKey) {
  try {
    const data = await apiFetch(`/jira/issue/${encodeURIComponent(issueKey)}/remotelinks`);
    return {
      confluenceLinks: Array.isArray(data?.confluenceLinks) ? data.confluenceLinks : [],
      webLinks: Array.isArray(data?.webLinks) ? data.webLinks : [],
    };
  } catch (err) {
    if (err instanceof AuthError) return { confluenceLinks: [], webLinks: [] };
    console.error(`Remote-link fetch failed for ${issueKey}:`, err);
    return { confluenceLinks: [], webLinks: [], error: true };
  }
}

/* ─── WRITE PATH (all return { success, message }) ──────────────────────── */

export async function jiraTransition(issueKey, transitionId) {
  try {
    const data = await apiFetch(`/jira/issue/${encodeURIComponent(issueKey)}/transition`, {
      method: "POST",
      body: { transitionId },
    });
    return { success: data?.success !== false, message: data?.message || "Transitioned." };
  } catch (err) {
    if (err instanceof AuthError) return { success: false, message: "Session expired — signing you back in." };
    return { success: false, message: err.message };
  }
}

export async function jiraComment(issueKey, commentText) {
  try {
    const data = await apiFetch(`/jira/issue/${encodeURIComponent(issueKey)}/comment`, {
      method: "POST",
      body: { body: commentText },
    });
    return { success: data?.success !== false, message: data?.message || "Comment added." };
  } catch (err) {
    if (err instanceof AuthError) return { success: false, message: "Session expired — signing you back in." };
    return { success: false, message: err.message };
  }
}

export async function jiraSetExecPriority(issueKey, value) {
  try {
    const data = await apiFetch(`/jira/issue/${encodeURIComponent(issueKey)}/exec-priority`, {
      method: "POST",
      body: { value },
    });
    return { success: data?.success !== false, message: data?.message || "Exec Priority set." };
  } catch (err) {
    if (err instanceof AuthError) return { success: false, message: "Session expired — signing you back in." };
    return { success: false, message: err.message };
  }
}

/* ─── CONFLUENCE (the one non-Jira seam) ────────────────────────────────────
   The "Save weekly snapshot" button publishes a Confluence page. Beyond the 5-function
   Jira contract; the BFF creates the page via Confluence REST (POST /wiki/api/v2/pages),
   so the 3LO app needs the write:confluence-content scope. Returns
   { success, pageId?, url?, message? } — same shape the snapshot handler expects. */
export async function confluenceCreatePage({ spaceId, parentId, title, html }) {
  try {
    const data = await apiFetch(`/confluence/page`, {
      method: "POST",
      body: { spaceId, parentId, title, html },
      timeoutMs: 30000, // page bodies can be large; give it a little more room
    });
    return {
      success: data?.success !== false,
      pageId: data?.pageId,
      url: data?.url,
      message: data?.message || (data?.success !== false ? "Snapshot saved to Confluence." : "Failed to save snapshot."),
    };
  } catch (err) {
    if (err instanceof AuthError) return { success: false, message: "Session expired — signing you back in." };
    return { success: false, error: err.message, message: err.message };
  }
}

/* ─── CONFLUENCE READ + UPDATE (Vision bar, v44) ────────────────────────────
   The Vision layer reads a page's storage-format body (parses the [VISION] block
   client-side) and writes edits back. Both act AS THE SIGNED-IN USER — Confluence
   page permissions are the authority gate, the BFF adds none.
   Requires the read:confluence-content scope (re-consent on next login). */

/* Returns { success, pageId, title, html, version, url } — html is the storage-
   format body VERBATIM. success:false (never a throw) on missing page/permission. */
export async function confluenceGetPage(pageId) {
  try {
    const data = await apiFetch(`/confluence/page/${encodeURIComponent(pageId)}`);
    return data || { success: false, message: "Empty response." };
  } catch (err) {
    if (err instanceof AuthError) return { success: false, message: "Session expired — signing you back in." };
    console.error("Confluence read failed:", err);
    return { success: false, error: err.message, message: err.message };
  }
}

/* Update a page's body (server handles the v2 version bump). Returns
   { success, pageId, version, url, message }; a version conflict comes back as
   success:false with a "reload and retry" message. */
export async function confluenceUpdatePage({ pageId, html, title }) {
  try {
    const data = await apiFetch(`/confluence/page/${encodeURIComponent(pageId)}`, {
      method: "PUT",
      body: { html, ...(title ? { title } : {}) },
      timeoutMs: 30000,
    });
    return {
      success: data?.success !== false,
      pageId: data?.pageId,
      version: data?.version,
      url: data?.url,
      message: data?.message || (data?.success !== false ? "Page updated." : "Failed to update page."),
    };
  } catch (err) {
    if (err instanceof AuthError) return { success: false, message: "Session expired — signing you back in." };
    return { success: false, error: err.message, message: err.message };
  }
}

/* ─── COHESION / CLOCKIN TIME (ENG-18) ──────────────────────────────────────
   Per-project time for the dashboard's task drill-down. In Eric's artifact this is
   a stub returning sample data; here it's real — the BFF reads ClockIn
   (prod_costar) over the RDS Data API. `projectId` is the "Cohesion Project" Jira
   field value, e.g. "COS_61". Returns the /projects-style expansion for one project:
   { projectId, projectName, hoursLogged, hoursBudgeted, percentConsumed,
     lastEntryDate,
     byMonth: [{period:"YYYY-MM", hours}],
     byPerson: [{name, hours, lastEntry}],
     recentEntries: [{workDate, name, hours, comment}],
     error }
   No cost/$ fields on this seam (not cleared for leadership-wide display).
   error:true (never a throw) on any failure so the panel renders its error state. */
export async function cohesionGetTime(projectId) {
  try {
    const data = await apiFetch(`/cohesion/${encodeURIComponent(projectId)}/time`);
    return data && !data.error ? data : { projectId, error: true };
  } catch (err) {
    if (err instanceof AuthError) return { projectId, error: true };
    console.error("Cohesion time fetch failed:", err);
    return { projectId, error: true };
  }
}

/* ─── /_UPLOAD PIPELINE (engineering-owned page, not part of Eric's artifact seam) ──
   Restricted to the two allowlisted emails server-side — a 403 here means the signed-in
   user isn't one of them, not a code bug. Both return { success, message, ... } like every
   other write function; the upload path also returns a `warnings` array when the port
   script found issues (nothing was published in that case). */

export async function uploadArtifact(contents, summary, version) {
  try {
    const data = await apiFetch(`/upload/artifact`, {
      method: "POST",
      body: { contents, summary, ...(version ? { version } : {}) },
      timeoutMs: 30000, // a full JSX source file + several GitHub API round trips
    });
    if (data?.error === "forbidden") return { success: false, message: "You don't have access to this page." };
    return data || { success: false, message: "Empty response." };
  } catch (err) {
    if (err instanceof AuthError) return { success: false, message: "Session expired — signing you back in." };
    return { success: false, message: err.message };
  }
}

export async function getUploadStatus() {
  try {
    const data = await apiFetch(`/upload/status`);
    if (data?.error === "forbidden") return { success: false, forbidden: true, message: "You don't have access to this page." };
    return data || { success: false, message: "Empty response." };
  } catch (err) {
    if (err instanceof AuthError) return { success: false, message: "Session expired — signing you back in." };
    return { success: false, message: err.message };
  }
}

/* Current production Dashboard.jsx (from main) + provenance — the reference file to
   copy components from when the canonical artifact drifts. */
export async function getCurrentDashboard() {
  try {
    const data = await apiFetch(`/upload/dashboard`, { timeoutMs: 30000 });
    if (data?.error === "forbidden") return { success: false, message: "You don't have access to this page." };
    return data || { success: false, message: "Empty response." };
  } catch (err) {
    if (err instanceof AuthError) return { success: false, message: "Session expired — signing you back in." };
    return { success: false, message: err.message };
  }
}

/* Revert to the previous published dashboard, via the same publish pipeline.
   confirm:false = preview only ({revertTo: {sha, message, date}}, nothing published);
   confirm:true  = actually republish the previous version. */
export async function revertArtifact(confirm) {
  try {
    const data = await apiFetch(`/upload/revert`, {
      method: "POST",
      body: { confirm: confirm === true },
      timeoutMs: 30000,
    });
    if (data?.error === "forbidden") return { success: false, message: "You don't have access to this page." };
    return data || { success: false, message: "Empty response." };
  } catch (err) {
    if (err instanceof AuthError) return { success: false, message: "Session expired — signing you back in." };
    return { success: false, message: err.message };
  }
}
